import Select, { SelectOptions, Option } from 'components/ui/Select'
import useMode from 'hooks/useMode'
import useOcrBuilders from 'hooks/useOcrBuilders'
import { useMemo } from 'react'
import { OcrBuilder } from 'types'
import { isEmpty } from 'utils/utils'

type GroupedOptions = Record<string, Option[]>

const pickSharedOptions = (ocrBuilders: OcrBuilder[]) =>
  ocrBuilders
    .filter(({ subGroup }) => !subGroup)
    .map(({ ocrBuilderConfigID, documentTypeName }) => ({
      value: ocrBuilderConfigID,
      label: documentTypeName,
    }))

const pickGroupedOptions = (ocrBuilders: OcrBuilder[]) => {
  const subGroupBuilders = ocrBuilders.filter(({ subGroup }) => !!subGroup)
  const groupedOptions: GroupedOptions = {}

  subGroupBuilders.forEach((builder) => {
    const { subGroup, ocrBuilderConfigID, documentTypeName } = builder
    if (!groupedOptions[subGroup]) {
      groupedOptions[subGroup] = []
    }
    groupedOptions[subGroup].push({ value: ocrBuilderConfigID, label: documentTypeName })
  })

  return groupedOptions
}

export interface OcrBuilderSelectorProps {
  ocrBuilderID: number | null
  onOcrBuilderIDChange: (ocrBuilderConfigID: number | null) => void
  disabled: boolean
}

export default function OcrBuilderSelector(props: OcrBuilderSelectorProps) {
  const { disabled, ocrBuilderID, onOcrBuilderIDChange } = props
  const { mode } = useMode()
  const { normalOcrBuilders, verifyOcrBuilders } = useOcrBuilders()
  const ocrBuilders = mode === 'normal' ? normalOcrBuilders : verifyOcrBuilders

  const ocrBuilderOptions: SelectOptions = useMemo(() => {
    if (!ocrBuilders.length) return []

    const sharedOptions = pickSharedOptions(ocrBuilders)
    const groupedOptions = pickGroupedOptions(ocrBuilders)

    let options: SelectOptions = []
    if (sharedOptions.length) {
      options.push({ label: '共通文書タイプ', options: sharedOptions })
    }
    if (!isEmpty(groupedOptions)) {
      options = options.concat(
        Object.entries(groupedOptions).map(([label, options]) => ({ label, options })),
      )
    }
    return options
  }, [ocrBuilders])

  return (
    <Select
      displayEmpty
      value={ocrBuilderID || ''}
      disabled={!ocrBuilders.length || disabled}
      options={ocrBuilderOptions}
      size="small"
      onChange={(e) => {
        onOcrBuilderIDChange(e.target.value)
      }}
      data-testid="document-type-template"
    />
  )
}
